import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import QuickEntryDialog from "@/components/QuickEntryDialog";
import SubscribeDialog from "@/components/SubscribeDialog";
import TrialBanner from "@/components/TrialBanner";
import LockedOverlay from "@/components/LockedOverlay";
import ReferralCard from "@/components/ReferralCard";
import MonthlyComparisonCard from "@/components/MonthlyComparisonCard";
import AccountHealthCard from "@/components/AccountHealthCard";
import CreditCardsHealthCard from "@/components/CreditCardsHealthCard";
import FinlyScoreCard from "@/components/FinlyScoreCard";
import WelcomeModal from "@/components/WelcomeModal";
import TrialRecapModal from "@/components/TrialRecapModal";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency, monthLabel } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Download,
  FileText,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  Info,
  CalendarDays,
  Target,
  Mic,
  Sparkles,
  AlertOctagon,
  ShieldCheck,
  Clock,
  ScanLine,
} from "lucide-react";
import { exportTransactionsCSV, exportTransactionsPDF } from "@/lib/export";

const PALETTE = ["#0052FF", "#EF4444", "#FBBF24", "#00E590", "#CBD5E1", "#3B82F6", "#DC2626", "#94A3B8"];

function KPICard({ label, value, currency, accent, icon: Icon, testId }) {
  return (
    <div className="clay-card p-6 fade-up" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
          {label}
        </span>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accent}15` }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="font-display text-3xl sm:text-4xl font-semibold tracking-tight font-numeric" style={{ color: accent }}>
        {formatCurrency(value, currency)}
      </div>
    </div>
  );
}

function RecommendationCard({ rec, idx }) {
  const config = {
    danger: { Icon: AlertTriangle, bg: "#FEE2E2", color: "#EF4444" },
    warning: { Icon: AlertTriangle, bg: "#FEF3C7", color: "#FBBF24" },
    success: { Icon: CheckCircle2, bg: "#D1FAE5", color: "#0052FF" },
    info: { Icon: Info, bg: "#F1F5F9", color: "#64748B" },
  };
  const c = config[rec.level] || config.info;
  return (
    <div
      className="rounded-lg p-4 fade-up border border-[#2A3447]"
      style={{ backgroundColor: c.bg, animationDelay: `${0.1 + idx * 0.05}s` }}
      data-testid={`recommendation-${idx}`}
    >
      <div className="flex gap-3">
        <c.Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: c.color }} />
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-semibold text-[#F1F5F9] text-sm mb-1">
            {rec.title}
          </h4>
          <p className="text-sm text-[#64748B] leading-relaxed">{rec.message}</p>
          {rec.consequence && (
            <p
              className="text-xs font-medium mt-2 px-2.5 py-1.5 rounded bg-white/60 border border-white/80 text-[#F1F5F9] leading-snug"
              data-testid={`recommendation-${idx}-consequence`}
            >
              <span className="uppercase tracking-[0.15em] text-[10px] block mb-0.5" style={{ color: c.color }}>
                Consecuencia
              </span>
              {rec.consequence}
            </p>
          )}
          {rec.action && (
            <p className="text-xs uppercase tracking-[0.15em] font-medium mt-2" style={{ color: c.color }}>
              → {rec.action}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const PRED_TONE = {
  success: { bg: "#D1FAE5", color: "#00C97D", Icon: TrendingDown },
  warning: { bg: "#FEF3C7", color: "#D97706", Icon: TrendingUp },
  info: { bg: "#E0E7FF", color: "#0052FF", Icon: Sparkles },
};

function PredictionCard({ data, currency }) {
  if (!data) {
    return (
      <div className="clay-card p-6 fade-up" data-testid="prediction-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#0052FF]" />
          <h3 className="font-display text-lg font-semibold text-[#F1F5F9]">
            Predicción mensual
          </h3>
        </div>
        <p className="text-sm text-[#94A3B8]">Cargando predicción…</p>
      </div>
    );
  }
  const tone = PRED_TONE[data.tone] || PRED_TONE.info;
  const confidencePct = Math.round((data.confidence || 0) * 100);
  const changePct = data.change_percent;
  return (
    <div className="clay-card p-6 fade-up" data-testid="prediction-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${tone.color}15` }}
          >
            <tone.Icon className="w-4 h-4" style={{ color: tone.color }} />
          </div>
          <h3 className="font-display text-lg font-semibold text-[#F1F5F9]">
            Predicción mensual
          </h3>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full font-medium"
          style={{ backgroundColor: `${tone.color}15`, color: tone.color }}
          data-testid="prediction-confidence"
        >
          {confidencePct}% confianza
        </span>
      </div>

      <div
        className="font-display text-3xl sm:text-4xl font-semibold tracking-tight font-numeric"
        style={{ color: tone.color }}
        data-testid="prediction-amount"
      >
        {formatCurrency(data.prediction || 0, currency)}
      </div>

      <p className="text-sm text-[#F1F5F9] mt-3 leading-relaxed" data-testid="prediction-headline">
        {data.headline}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg bg-[#131C2E] border border-[#2A3447] p-3">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] mb-1">
            Mes anterior
          </p>
          <p className="font-numeric font-semibold text-sm text-[#F1F5F9]" data-testid="prediction-prev-month">
            {formatCurrency(data.previous_month_total || 0, currency)}
          </p>
        </div>
        <div className="rounded-lg bg-[#131C2E] border border-[#2A3447] p-3">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] mb-1">
            Variación
          </p>
          <p
            className="font-numeric font-semibold text-sm"
            style={{
              color:
                (data.change_absolute || 0) > 0
                  ? "#EF4444"
                  : (data.change_absolute || 0) < 0
                  ? "#00C97D"
                  : "#64748B",
            }}
            data-testid="prediction-change"
          >
            {(data.change_absolute || 0) >= 0 ? "+" : ""}
            {formatCurrency(data.change_absolute || 0, currency)}
            {changePct !== null && changePct !== undefined
              ? ` (${changePct > 0 ? "+" : ""}${changePct}%)`
              : ""}
          </p>
        </div>
      </div>

      <p className="text-[11px] text-[#94A3B8] mt-3 font-numeric">
        MTD: {formatCurrency(data.mtd_total || 0, currency)} · Día{" "}
        {data.days_elapsed}/{data.days_in_month}
      </p>

      {(data.confidence ?? 1) < 0.4 && (
        <div
          className="mt-4 flex items-start gap-2.5 rounded-lg border border-[#E6C875]/40 bg-[#FFFAEB] px-3 py-2.5"
          data-testid="prediction-low-confidence-banner"
        >
          <svg
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#B7791F"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-[12px] leading-snug text-[#7C5A12]">
            Agrega más movimientos para mejorar la predicción.
          </p>
        </div>
      )}
    </div>
  );
}

function AnomalyCard({ data, currency }) {
  if (!data) {
    return (
      <div className="clay-card p-6 fade-up" data-testid="anomalies-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <AlertOctagon className="w-5 h-5 text-[#D97706]" />
          <h3 className="font-display text-lg font-semibold text-[#F1F5F9]">
            Gastos anómalos
          </h3>
        </div>
        <p className="text-sm text-[#94A3B8]">Cargando análisis…</p>
      </div>
    );
  }
  const hasAnomalies = (data.anomaly_count || 0) > 0;
  const accent = hasAnomalies ? "#D97706" : "#00C97D";
  const Icon = hasAnomalies ? AlertOctagon : ShieldCheck;
  return (
    <div className="clay-card p-6 fade-up" data-testid="anomalies-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accent}15` }}
          >
            <Icon className="w-4 h-4" style={{ color: accent }} />
          </div>
          <h3 className="font-display text-lg font-semibold text-[#F1F5F9]">
            Gastos anómalos
          </h3>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full font-medium"
          style={{ backgroundColor: `${accent}15`, color: accent }}
          data-testid="anomalies-count-badge"
        >
          {data.anomaly_count} detectados
        </span>
      </div>

      <p className="text-sm text-[#F1F5F9] leading-relaxed mb-4" data-testid="anomalies-headline">
        {data.headline}
      </p>

      {hasAnomalies ? (
        <ul className="space-y-2" data-testid="anomalies-list">
          {(data.anomalies || []).map((a, i) => (
            <li
              key={a.transaction_id || i}
              className="rounded-lg border border-[#2A3447] bg-[#131C2E] p-3 flex items-center gap-3"
              data-testid={`anomaly-item-${i}`}
            >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "#FEF3C7" }}
              >
                <AlertOctagon className="w-4 h-4 text-[#D97706]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#F1F5F9] truncate">
                  {a.merchant || a.note || a.category}
                </p>
                <p className="text-[11px] text-[#94A3B8] truncate font-numeric">
                  {a.category} · {a.date} · {a.ratio_vs_mean.toFixed(1)}× tu promedio
                </p>
              </div>
              <span
                className="font-numeric font-semibold text-sm text-[#EF4444] whitespace-nowrap"
                data-testid={`anomaly-item-${i}-amount`}
              >
                {formatCurrency(a.amount, currency)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-dashed border-[#2A3447] p-4 text-center">
          <p className="text-xs text-[#94A3B8] uppercase tracking-[0.18em]">
            Patrón de gasto saludable
          </p>
        </div>
      )}

      {data.evaluated_categories > 0 && (
        <p className="text-[11px] text-[#94A3B8] mt-3 font-numeric">
          Evaluado {data.evaluated_categories} categoría
          {data.evaluated_categories === 1 ? "" : "s"} · ventana {data.window_days} días
        </p>
      )}
    </div>
  );
}

function SmartCaptureHook({ stats, onTryVoice, onTryOcr }) {
  const monthCount = stats?.month_count || 0;
  const totalCount = stats?.total_count || 0;
  const minutesMonth = stats?.minutes_saved_month || 0;
  const minutesTotal = stats?.minutes_saved_total || 0;

  const hasActivity = totalCount > 0;

  const formatTime = (mins) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
  };

  return (
    <div
      className="clay-card p-6 fade-up relative overflow-hidden"
      data-testid="smart-capture-hook"
    >
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, #00E590 0%, #0052FF 60%, transparent 70%)",
        }}
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#0052FF] to-[#00C97D] flex-shrink-0">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] font-medium">
                Tiempo ahorrado
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#00E590]/15 text-[#00C97D] font-semibold">
                Voz + OCR
              </span>
            </div>
            {hasActivity ? (
              <>
                <p
                  className="font-display text-2xl sm:text-3xl font-semibold text-[#F1F5F9] tracking-tight font-numeric"
                  data-testid="smart-capture-headline"
                >
                  Te ahorraste{" "}
                  <span style={{ color: "#00C97D" }}>{formatTime(minutesMonth)}</span>{" "}
                  este mes
                </p>
                <p className="text-sm text-[#64748B] mt-1 font-numeric" data-testid="smart-capture-subline">
                  {monthCount} {monthCount === 1 ? "captura inteligente" : "capturas inteligentes"} este mes
                  {" · Total: "}
                  <span className="font-semibold text-[#F1F5F9]">{totalCount}</span>
                  {" · "}
                  <span className="font-semibold text-[#F1F5F9]">{formatTime(minutesTotal)}</span>
                  {" ahorrados"}
                </p>
              </>
            ) : (
              <>
                <p className="font-display text-xl sm:text-2xl font-semibold text-[#F1F5F9] tracking-tight">
                  Registra en segundos con voz o foto
                </p>
                <p className="text-sm text-[#64748B] mt-1">
                  Cada captura inteligente te ahorra ~2 minutos. Prueba ahora.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 gap-2">
          <Button
            variant="outline"
            className="border-[#2A3447] bg-[#131C2E] hover:bg-[#1B2638] h-10"
            onClick={onTryVoice}
            data-testid="smart-capture-try-voice"
          >
            <Mic className="w-4 h-4 mr-2" />
            Voz
          </Button>
          <Button
            variant="outline"
            className="border-[#2A3447] bg-[#131C2E] hover:bg-[#1B2638] h-10"
            onClick={onTryOcr}
            data-testid="smart-capture-try-ocr"
          >
            <ScanLine className="w-4 h-4 mr-2" />
            Recibo
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isPremium, subscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const isLocked = !isPremium; // Admin and active/trial users are premium
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [insights, setInsights] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [smartStats, setSmartStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeChecked, setWelcomeChecked] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, c] = await Promise.all([
        api.get(`/summary?month=${month}&year=${year}`),
        api.get(`/transactions?month=${month}&year=${year}`),
        api.get(`/categories`),
      ]);
      setSummary(s.data);
      setTransactions(t.data);
      setCategories(c.data);
      // Smart-capture stats (voice + OCR time saved) — independent of month filter
      try {
        const ss = await api.get(`/transactions/smart-capture-stats`);
        setSmartStats(ss.data);
      } catch (_) {
        setSmartStats(null);
      }
    } catch (err) {
      // 401 is handled globally by the axios interceptor (redirect to /login).
      // For any other error, surface a soft toast so the UI doesn't crash.
      if (err?.response?.status !== 401) {
        toast.error(
          formatApiErrorDetail(err?.response?.data?.detail) ||
            "No se pudieron cargar tus datos. Inténtalo de nuevo."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  const loadInsights = useCallback(async () => {
    if (isLocked) {
      setInsights(null);
      return;
    }
    try {
      const r = await api.get(`/insights?month=${month}&year=${year}`);
      setInsights(r.data);
    } catch (_) {
      setInsights(null);
    }
  }, [month, year, isLocked]);

  const loadPredictionAndAnomalies = useCallback(async () => {
    if (isLocked) {
      setPrediction(null);
      setAnomalies(null);
      return;
    }
    try {
      const [p, a] = await Promise.all([
        api.get(`/insights/predict-monthly?month=${month}&year=${year}`),
        api.get(`/insights/anomalies?window_days=90`),
      ]);
      setPrediction(p.data);
      setAnomalies(a.data);
      // One-shot toast per session when anomalies are detected
      const count = a.data?.anomaly_count || 0;
      if (count > 0) {
        const key = `finly_anom_toast_${month}_${year}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          const first = a.data.anomalies?.[0];
          toast.warning(
            `${count} ${count === 1 ? "gasto anómalo detectado" : "gastos anómalos detectados"}`,
            {
              description: first
                ? `${first.merchant || first.category}: ${first.ratio_vs_mean.toFixed(1)}× tu promedio en ${first.category}. Revísalo en el panel.`
                : a.data.headline,
              duration: 7000,
              action: {
                label: "Ver",
                onClick: () => {
                  const el = document.querySelector('[data-testid="anomalies-card"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                },
              },
            },
          );
        }
      }
    } catch (_) {
      setPrediction(null);
      setAnomalies(null);
    }
  }, [month, year, isLocked]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  useEffect(() => {
    loadPredictionAndAnomalies();
  }, [loadPredictionAndAnomalies]);

  // Refresh data whenever the global Voice FAB creates a transaction
  useEffect(() => {
    const reload = () => {
      loadData();
      loadInsights();
      loadPredictionAndAnomalies();
    };
    window.addEventListener("finly:transaction-created", reload);
    return () => window.removeEventListener("finly:transaction-created", reload);
  }, [loadData, loadInsights, loadPredictionAndAnomalies]);

  // Welcome tour: show once for brand-new users with zero transactions
  useEffect(() => {
    if (welcomeChecked || loading || !user?.id) return;
    const key = `finly_welcome_done_${user.id}`;
    if (localStorage.getItem(key) === "1") {
      setWelcomeChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/transactions?limit=1`);
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length === 0) setWelcomeOpen(true);
      } catch (_) {
        // silent fail — modal just won't show this session
      } finally {
        if (!cancelled) setWelcomeChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [welcomeChecked, loading, user?.id]);

  const markWelcomeDone = useCallback(() => {
    if (user?.id) localStorage.setItem(`finly_welcome_done_${user.id}`, "1");
  }, [user?.id]);

  const currency = summary?.currency || "MXN";
  const balance = summary?.balance || 0;
  const balanceColor = balance >= 0 ? "#0052FF" : "#EF4444";

  const chartData = (summary?.group_breakdown || []).map((g, i) => ({
    name: g.group,
    value: g.amount,
    color: PALETTE[i % PALETTE.length],
  }));

  const years = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) years.push(y);
  const months = [
    { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" }, { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
    { value: 7, label: "Julio" }, { value: 8, label: "Agosto" }, { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
  ];

  return (
    <Layout>
      <TrialBanner />

      {/* Hero - Balance */}
      <section className="mb-10 fade-up" data-testid="balance-hero">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] mb-3 font-medium">
              Saldo del Mes · {monthLabel(month, year)}
            </p>
            <div
              className="font-display text-5xl sm:text-7xl font-bold tracking-tighter font-numeric"
              style={{ color: balanceColor }}
              data-testid="balance-amount"
            >
              {formatCurrency(balance, currency)}
            </div>
            <p className="text-sm text-[#64748B] mt-2 font-numeric">
              {summary?.transaction_count || 0} movimientos registrados
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-[140px] bg-[#131C2E] border-[#2A3447] h-11" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E]">
                {months.map((m) => (
                  <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[100px] bg-[#131C2E] border-[#2A3447] h-11" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E]">
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-[#2A3447] bg-[#131C2E] hover:bg-[#1B2638] h-11"
                  data-testid="export-menu-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#131C2E]">
                <DropdownMenuItem
                  onClick={() => exportTransactionsCSV(transactions, currency)}
                  data-testid="export-csv"
                >
                  <FileText className="w-4 h-4 mr-2" /> CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => exportTransactionsPDF(transactions, summary, month, year, currency)}
                  data-testid="export-pdf"
                >
                  <FileText className="w-4 h-4 mr-2" /> PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <QuickEntryDialog
              categories={categories}
              onCreated={loadData}
              open={quickEntryOpen}
              onOpenChange={setQuickEntryOpen}
            />
          </div>
        </div>
      </section>

      <WelcomeModal
        open={welcomeOpen}
        onOpenChange={setWelcomeOpen}
        onComplete={markWelcomeDone}
        onAddExpense={() => setQuickEntryOpen(true)}
        onCreateBudget={() => navigate("/budgets")}
        onTryVoice={() => window.dispatchEvent(new CustomEvent("finly:open-voice"))}
      />

      <SubscribeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />

      <TrialRecapModal onUpgrade={() => setUpgradeOpen(true)} />

      {/* Finly Score (Premium) — above KPIs */}
      <section className="mb-6">
        <LockedOverlay locked={isLocked} testId="locked-finly-score" label="Finly Score · Premium">
          <FinlyScoreCard month={month} year={year} />
        </LockedOverlay>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        <KPICard
          label="Ingresos"
          value={summary?.total_income || 0}
          currency={currency}
          accent="#0052FF"
          icon={TrendingUp}
          testId="kpi-income"
        />
        <KPICard
          label="Gastos"
          value={summary?.total_expense || 0}
          currency={currency}
          accent="#EF4444"
          icon={TrendingDown}
          testId="kpi-expense"
        />
        <KPICard
          label="Saldo"
          value={summary?.balance || 0}
          currency={currency}
          accent={balanceColor}
          icon={Wallet}
          testId="kpi-balance"
        />
      </section>

      {/* Tiempo Ahorrado · retention hook (Voz + OCR) */}
      <section className="mb-6">
        <SmartCaptureHook
          stats={smartStats}
          onTryVoice={() =>
            window.dispatchEvent(new CustomEvent("finly:open-voice"))
          }
          onTryOcr={() => {
            navigate("/transactions");
            setTimeout(
              () => window.dispatchEvent(new CustomEvent("finly:open-ocr")),
              150,
            );
          }}
        />
      </section>

      {/* Premium KPIs: Daily money + Projection */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
        <LockedOverlay locked={isLocked} testId="locked-daily">
          <div className="clay-card p-6 fade-up" data-testid="kpi-daily-available">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Disponible por día
              </span>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#00E590]/15">
                <CalendarDays className="w-4 h-4 text-[#00C97D]" />
              </div>
            </div>
            <div
              className="font-display text-3xl sm:text-4xl font-semibold tracking-tight font-numeric"
              style={{ color: (insights?.daily_available ?? 0) >= 0 ? "#00C97D" : "#EF4444" }}
              data-testid="kpi-daily-available-amount"
            >
              {formatCurrency(insights?.daily_available || 0, currency)}
            </div>
            <p className="text-xs text-[#64748B] mt-2 font-numeric">
              {insights
                ? `${insights.days_remaining} días restantes en el mes`
                : "—"}
            </p>
          </div>
        </LockedOverlay>

        <LockedOverlay locked={isLocked} testId="locked-projection">
          <div className="clay-card p-6 fade-up" data-testid="kpi-projection">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Proyección de fin de mes
              </span>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0052FF]/15">
                <Target className="w-4 h-4 text-[#0052FF]" />
              </div>
            </div>
            <div
              className="font-display text-3xl sm:text-4xl font-semibold tracking-tight font-numeric flex items-center gap-2"
              style={{ color: (insights?.projected_end_balance ?? 0) >= 0 ? "#0052FF" : "#EF4444" }}
              data-testid="kpi-projection-amount"
            >
              {(insights?.projected_end_balance ?? 0) >= 0 ? (
                <TrendingUp className="w-6 h-6" />
              ) : (
                <TrendingDown className="w-6 h-6" />
              )}
              {formatCurrency(insights?.projected_end_balance || 0, currency)}
            </div>
            <p className="text-xs text-[#64748B] mt-2 font-numeric">
              {insights
                ? `Promedio: ${formatCurrency(insights.avg_daily_spend, currency)} / día`
                : "—"}
            </p>
          </div>
        </LockedOverlay>
      </section>

      {/* Premium insights: Monthly prediction (A2) + Anomalies (A3) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6" data-testid="premium-insights-section">
        <LockedOverlay locked={isLocked} testId="locked-prediction" label="Predicción mensual · Premium">
          <PredictionCard data={prediction} currency={currency} />
        </LockedOverlay>
        <LockedOverlay locked={isLocked} testId="locked-anomalies" label="Gastos anómalos · Premium">
          <AnomalyCard data={anomalies} currency={currency} />
        </LockedOverlay>
      </section>

      {/* Monthly comparison widget */}
      <section className="mb-10">
        <MonthlyComparisonCard month={month} year={year} />
      </section>

      {/* Account health widget — spend per account this month */}
      <section className="mb-10">
        <AccountHealthCard month={month} year={year} currency={currency} />
      </section>

      {/* Credit-cards health widget — semaphore + days to payment */}
      <section className="mb-10">
        <CreditCardsHealthCard currency={currency} />
      </section>

      {/* Chart + Recommendations */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="clay-card p-6 lg:col-span-2 fade-up" data-testid="expenses-chart-card">          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-xl font-semibold text-[#F1F5F9]">
                Distribución de Gastos
              </h2>
              <p className="text-sm text-[#64748B]">Por categoría agrupada</p>
            </div>
          </div>
          {chartData.length === 0 ? (
            <div className="h-80 flex flex-col items-center justify-center text-center">
              <p className="text-[#94A3B8] mb-3">Sin gastos registrados en este periodo</p>
              <p className="text-xs text-[#94A3B8] uppercase tracking-[0.2em]">
                Registra tu primer movimiento
              </p>
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.color} stroke="#FFFFFF" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatCurrency(v, currency)}
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      borderRadius: 8,
                      fontFamily: "IBM Plex Sans",
                    }}
                  />
                  <Legend
                    iconType="circle"
                    formatter={(v) => <span style={{ color: "#64748B", fontSize: 13 }}>{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <LockedOverlay locked={isLocked} testId="locked-recommendations">
          <div className="clay-card-inset p-6 fade-up" data-testid="recommendations-panel">
            <div className="flex items-center gap-2 mb-5">
              <Lightbulb className="w-5 h-5 text-[#FBBF24]" />
              <h2 className="font-display text-xl font-semibold text-[#F1F5F9]">
                Recomendaciones
              </h2>
            </div>
            <div className="space-y-3">
              {(insights?.recommendations || []).map((rec, i) => (
                <RecommendationCard key={i} rec={rec} idx={i} />
              ))}
              {(!insights?.recommendations || insights.recommendations.length === 0) && (
                <p className="text-sm text-[#94A3B8]">Sin recomendaciones por ahora.</p>
              )}
            </div>
          </div>
        </LockedOverlay>
      </section>

      {/* Category breakdown */}
      {summary?.category_breakdown && summary.category_breakdown.length > 0 && (
        <section className="clay-card p-6 mb-10 fade-up" data-testid="category-breakdown">
          <h2 className="font-display text-xl font-semibold text-[#F1F5F9] mb-5">
            Desglose por categoría
          </h2>
          <div className="space-y-3">
            {summary.category_breakdown.slice(0, 10).map((cat, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-[#F1F5F9]">{cat.category}</span>
                    <span className="font-numeric text-sm font-semibold text-[#F1F5F9]">
                      {formatCurrency(cat.amount, currency)}
                    </span>
                  </div>
                  <div className="h-2 bg-[#1B2638] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(cat.percentage_of_income, 100)}%`,
                        backgroundColor: PALETTE[i % PALETTE.length],
                      }}
                    />
                  </div>
                </div>
                <span className="text-xs font-numeric text-[#94A3B8] w-12 text-right">
                  {cat.percentage_of_income.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Referral CTA */}
      <ReferralCard />

      {/* Recent transactions */}
      <section className="clay-card p-6 fade-up" data-testid="recent-transactions">
        <h2 className="font-display text-xl font-semibold text-[#F1F5F9] mb-5">
          Movimientos recientes
        </h2>
        {transactions.length === 0 ? (
          loading ? (
            <p className="text-sm text-[#94A3B8] py-8 text-center">Cargando...</p>
          ) : (
            <div
              className="py-8 px-4 sm:px-8 text-center flex flex-col items-center gap-5"
              data-testid="empty-transactions-state"
            >
              <div className="w-14 h-14 rounded-full bg-[#0052FF]/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#0052FF]" />
              </div>
              <div className="space-y-2 max-w-md">
                <h3 className="font-display text-xl font-semibold text-[#F1F5F9]">
                  Empieza a registrar tu dinero
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  Aún no tienes movimientos en {monthLabel(month, year)}. Tienes dos formas rápidas de empezar:
                </p>
              </div>
              <ol className="text-left text-sm text-[#F1F5F9] space-y-2.5 max-w-md w-full">
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0052FF] text-white text-xs font-semibold flex items-center justify-center">1</span>
                  <span>
                    Toca <strong className="font-semibold">Nuevo movimiento</strong> para agregar un ingreso o gasto manualmente (monto, categoría y fecha).
                  </span>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EF4444] text-white text-xs font-semibold flex items-center justify-center">2</span>
                  <span>
                    O usa <strong className="font-semibold">Voz</strong> y dictá algo como <em>"gasté 250 pesos en gasolina"</em> — Finly lo registra solo.
                  </span>
                </li>
              </ol>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  onClick={() => setQuickEntryOpen(true)}
                  className="bg-[#0052FF] hover:bg-[#0040CC] text-white h-11 px-6"
                  data-testid="empty-state-add-button"
                >
                  Agregar mi primer movimiento
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.dispatchEvent(new CustomEvent("finly:open-voice"))}
                  className="h-11 px-6 border-[#2A3447] text-[#F1F5F9]"
                  data-testid="empty-state-voice-button"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Probar con voz
                </Button>
              </div>
              <p className="text-xs text-[#94A3B8] pt-2">
                Tip: una vez registres tus primeros movimientos, verás tu balance, gráficas y tu Finly Score actualizados al instante.
              </p>
            </div>
          )
        ) : (
          <div className="divide-y divide-[#E2E8F0]">
            {transactions.slice(0, 8).map((t) => (
              <div key={t.id} className="py-3 flex items-center gap-4" data-testid={`tx-row-${t.id}`}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: t.type === "income" ? "#D1FAE5" : "#FEE2E2",
                  }}
                >
                  {t.type === "income" ? (
                    <TrendingUp className="w-4 h-4 text-[#0052FF]" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-[#EF4444]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#F1F5F9] truncate">{t.category}</p>
                  <p className="text-xs text-[#94A3B8] truncate">
                    {t.date} {t.note ? `· ${t.note}` : ""}
                  </p>
                </div>
                <span
                  className="font-numeric font-semibold text-sm"
                  style={{ color: t.type === "income" ? "#0052FF" : "#EF4444" }}
                >
                  {t.type === "income" ? "+" : "-"}
                  {formatCurrency(t.amount, currency)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
